import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pageTabsSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import DVLAAnalyticsDashboard from '../../components/DVLAAnalyticsDashboard';
import DVLAStaffTable from '../../components/DVLAStaffTable';
import DVLASettings from '../../components/DVLASettings';
import DVLALicenseRequestsTable from '../../components/DVLALicenseRequestsTable';
import DVLANotifications from '../../components/DVLANotifications';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dvla-tabpanel-${index}`}
      aria-labelledby={`dvla-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const DVLA_TABS = ['staff', 'requests', 'analytics', 'notifications', 'settings'] as const;

const DVLA: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    const idx = DVLA_TABS.indexOf(t as (typeof DVLA_TABS)[number]);
    return idx >= 0 ? idx : 0;
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (!t) return;
    const idx = DVLA_TABS.indexOf(t as (typeof DVLA_TABS)[number]);
    if (idx >= 0) setTabValue(idx);
  }, [searchParams]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: DVLA_TABS[newValue] }, { replace: true });
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader title="DVLA" subtitle="Compliance" />

      <Paper sx={{ borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={pageTabsSx}
        >
          <Tab label="Staff Management" />
          <Tab label="License Requests" />
          <Tab label="Analytics Dashboard" />
          <Tab label="Notifications" />
          <Tab label="Settings" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <DVLAStaffTable key={`staff-${refreshKey}`} onRefresh={handleRefresh} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <DVLALicenseRequestsTable key={`requests-${refreshKey}`} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <DVLAAnalyticsDashboard key={`analytics-${refreshKey}`} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <DVLANotifications key={`notifications-${refreshKey}`} />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <DVLASettings />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default DVLA;
